// @file Surface comments store
import { trackEvent } from '@@/bits/analytics'
import { CONFIRM_MODERATION_REJECT_COMMENT, SELF_HARM_COMMENT_ALERT } from '@@/bits/confirmation_dialog'
import { captureFetchException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import { COMMENT_BODY_CHAR_LIMIT } from '@@/bits/numbers'
import { getVuexStore } from '@@/bits/pinia'
import { selfHarmHelplineUrl } from '@@/bits/url'
import { SnackbarNotificationType } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useNativeAppStore } from '@@/pinia/native_app'
import PadletApi from '@@/surface/padlet_api'
import type { Cid, Comment, CommentId, Id, Post, User } from '@@/types'
import { OzConfirmationDialogBoxButtonScheme } from '@@/vuexstore/modules/global_confirmation_dialog'
import type { RootState as SurfaceState } from '@@/vuexstore/surface/types'
import type { JsonAPIResource } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed } from 'vue'

export const useCommentsStore = defineStore('comments', () => {
  const surfaceVuexStore = getVuexStore<SurfaceState>()
  const globalSnackbarStore = useGlobalSnackbarStore()

  const user = computed<User>(() => surfaceVuexStore?.getters.user)
  const commentIds = computed<string[]>(() => surfaceVuexStore?.getters['comment/commentIds'])
  const commentIdsByPost = computed<{ [wishId: number]: CommentId[] }>(
    () => surfaceVuexStore?.getters['comment/commentIdsByPost'],
  )
  const commentsByPostId = computed<Record<Id, Comment[]>>(() => surfaceVuexStore?.getters['comment/commentsByPost'])
  const isCommenting = computed<boolean>(() => surfaceVuexStore?.getters['comment/isCommenting'])
  const inlineCommentInputLastTypedInMs = computed<number>(
    () => surfaceVuexStore?.state.comment.inlineCommentInputLastTypedInMs ?? 0,
  )
  const commentEntities = computed<Record<Id, Comment>>(() => surfaceVuexStore?.state.comment.commentEntities ?? {})
  const commentBeingEditedId = computed<CommentId | null>(
    () => surfaceVuexStore?.state.comment.commentBeingEditedId ?? null,
  )
  const commentUnderActionId = computed<CommentId | null>(
    () => surfaceVuexStore?.state.comment.actionMenuCommentId ?? null,
  )
  const activeNewCommentPostId = computed<Id | null>(
    () => surfaceVuexStore?.state.comment.activeNewCommentPostId ?? null,
  )

  const newCommentBodyForPostCid = (postCid: Cid): string => {
    return surfaceVuexStore?.getters['comment/newCommentBodyForPostCid'](postCid)
  }

  const editedDraftCommentBodyFromId = (commentId: Id): string => {
    return surfaceVuexStore?.getters['comment/editedDraftCommentBodyFromId'](commentId)
  }

  async function createCommentV2({ postId, htmlBody }: { postId: Id; htmlBody: string }): Promise<void> {
    if (htmlBody.length > COMMENT_BODY_CHAR_LIMIT) {
      showCommentCharacterLimitSnackbar()
      return
    }

    // tempId will be used to identify the comment in the frontend until it is persisted,
    // after which, we will use the server-provided Id.
    const tempId = `new_${Date.now()}`

    const newComment = {
      wish_id: postId,
      html_body: htmlBody,
      user_id: user.value.id,
      user_hashid: user.value.hashid,
    }
    surfaceVuexStore?.commit('comment/ADD_PENDING_COMMENT', { id: tempId, ...newComment })

    try {
      const post = surfaceVuexStore?.getters['post/getPostByServerId'](postId)
      void surfaceVuexStore?.dispatch('comment/updateNewCommentBody', { postCid: post.cid, body: '' })

      const response = await PadletApi.CommentV2.create(newComment)
      const comment = (response.data as JsonAPIResource<Comment>).attributes

      surfaceVuexStore?.commit('comment/ADD_COMMENT', { comment, tempId })
    } catch (e) {
      void globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SurfaceCreateCommentV2' })
      const post = surfaceVuexStore?.getters['post/getPostByServerId'](postId)
      if (post == null || post.cid == null) {
        void surfaceVuexStore?.commit('comment/REMOVE_PENDING_COMMENT', { commentId: tempId })
      } else {
        void surfaceVuexStore?.commit('comment/REVERT_PENDING_COMMENT', { commentId: tempId, postCid: post.cid })
      }
    } finally {
      void surfaceVuexStore?.dispatch('realtime/dispatchAllQueuedActions', null, { root: true })
      void useNativeAppStore().postSurfaceState()
    }
  }

  function startEditingCommentV2({ commentId }: { commentId: CommentId }): void {
    surfaceVuexStore?.commit('comment/START_EDITING_COMMENT', commentId)
    void surfaceVuexStore?.dispatch('comment/updateEditedDraftCommentBody', {
      commentId,
      body: commentEntities.value[commentId].html_body ?? commentEntities.value[commentId].body,
    })
  }

  async function editCommentV2({ id, htmlBody }: { id: Id; htmlBody: string }): Promise<void> {
    // Comparing to existingComment so that only comments that already exceed character limit can continue to exceed character limit
    const existingCommentBody = commentEntities.value[id]?.html_body ?? ''
    if (existingCommentBody.length <= COMMENT_BODY_CHAR_LIMIT && htmlBody.length > COMMENT_BODY_CHAR_LIMIT) {
      showCommentCharacterLimitSnackbar()
      return
    }

    try {
      void surfaceVuexStore?.commit('comment/UPDATE_COMMENT', { id, obj: { html_body: htmlBody } })
      void surfaceVuexStore?.commit('comment/STOP_EDITING_COMMENT')
      const {
        data: {
          attributes: { html_body: newBody },
        },
      } = await PadletApi.CommentV2.update(id, { html_body: htmlBody })
      void surfaceVuexStore?.commit('comment/UPDATE_COMMENT', { id, obj: { html_body: newBody } })
    } catch (e) {
      void globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SurfaceEditCommentV2' })
      startEditingCommentV2({ commentId: id })
    } finally {
      void surfaceVuexStore?.dispatch('realtime/dispatchAllQueuedActions', null, { root: true })
      void useNativeAppStore().postSurfaceState()
    }
  }

  async function approveComment(comment: Comment): Promise<void> {
    const {
      data: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        attributes: { is_awaiting_approval },
      },
    } = await PadletApi.CommentV2.approve(Number(comment.id))
    void surfaceVuexStore?.commit('comment/UPDATE_COMMENT', {
      id: comment.id,
      obj: { is_awaiting_approval },
    })
  }

  async function rejectComment(comment: Comment): Promise<void> {
    void surfaceVuexStore?.dispatch(
      'confirmationDialog/openConfirmationDialog',
      {
        ...CONFIRM_MODERATION_REJECT_COMMENT,
        afterConfirmActions: [
          async () => {
            void surfaceVuexStore?.commit('comment/REMOVE_COMMENT', comment.id)
            await PadletApi.Comment.delete(comment)
          },
        ],
      },
      { root: true },
    )
  }

  function showCommentCharacterLimitSnackbar(): void {
    void globalSnackbarStore.setSnackbar({
      message: __('Comment character limit reached'),
      notificationType: SnackbarNotificationType.error,
    })
  }

  function askToDeleteComment({ commentId }: { commentId: Id }): void {
    void surfaceVuexStore?.dispatch(
      'confirmationDialog/openConfirmationDialog',
      {
        title: __('Delete this comment?'),
        body: __('Are you sure you want to delete this comment? This cannot be undone!'),
        confirmButtonText: __('Delete'),
        cancelButtonText: __('Cancel'),
        buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
        afterConfirmActions: [{ type: 'comment/deleteComment', payload: { commentId } }],
      },
      { root: true },
    )
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  function showSelfHarmCommentAlert({ comment_id }: { comment_id?: Id }): void {
    if (comment_id == null) return

    trackEvent('SurfaceSelfHarmWarning', 'Showed Self-harm dialog for comment', comment_id)
    void surfaceVuexStore?.dispatch(
      'confirmationDialog/openConfirmationDialog',
      {
        ...SELF_HARM_COMMENT_ALERT,
        afterConfirmActions: [() => navigateTo(selfHarmHelplineUrl(), { target: 'blank' })],
      },
      {
        root: true,
      },
    )
  }

  /* ---------------------- */
  /* VUEX ACTIONS           */
  /* ---------------------- */

  function fetchComments(payload: { shouldResetState: boolean }): void {
    void surfaceVuexStore?.dispatch('comment/fetchComments', payload)
  }

  function refreshComments(): void {
    void surfaceVuexStore?.dispatch('comment/refreshComments')
  }

  function showCommentActionMenu(payload: { commentId: CommentId }): void {
    void surfaceVuexStore?.dispatch('comment/showCommentActionMenu', payload)
  }

  function hideCommentActionMenu(): void {
    void surfaceVuexStore?.dispatch('comment/hideCommentActionMenu')
  }

  function startEditingComment(payload: { commentId: CommentId }): void {
    void surfaceVuexStore?.dispatch('comment/startEditingComment', payload)
  }

  function stopEditingComment(): void {
    void surfaceVuexStore?.dispatch('comment/stopEditingComment')
  }

  function startWritingNewComment(payload: { postId: Id }): void {
    void surfaceVuexStore?.dispatch('comment/startWritingNewComment', payload)
  }

  function stopWritingNewComment(payload: { postId: Id }): void {
    void surfaceVuexStore?.dispatch('comment/stopWritingNewComment', payload)
  }

  function resetWritingNewComment(payload: { post: Post }): void {
    void surfaceVuexStore?.dispatch('comment/updateEditedDraftCommentBody', payload)
  }

  function deleteComment(payload: { commentId: CommentId }): void {
    void surfaceVuexStore?.dispatch('comment/deleteComment', payload)
  }

  function newCommentRemote(payload: { comment: Comment }): void {
    void surfaceVuexStore?.dispatch('comment/newCommentRemote', payload)
  }

  function editCommentRemote(payload: { comment: Comment }): void {
    void surfaceVuexStore?.dispatch('comment/editCommentRemote', payload)
  }

  function deleteCommentRemote(payload: { comment: Comment }): void {
    void surfaceVuexStore?.dispatch('comment/deleteCommentRemote', payload)
  }

  function updateNewCommentBody(payload: { postCid: Cid; body: string }): void {
    void surfaceVuexStore?.dispatch('comment/updateNewCommentBody', payload)
  }

  function updateEditedDraftCommentBody(payload: { commentId: Id; body: string }): void {
    void surfaceVuexStore?.dispatch('comment/updateEditedDraftCommentBody', payload)
  }

  function updateInlineCommentInputLastTypedInMs(): void {
    void surfaceVuexStore?.dispatch('comment/updateInlineCommentInputLastTypedInMs')
  }

  /* ---------------------- */
  /* COMMENTS PANEL         */
  /* ---------------------- */
  const xCommentsPanel = computed<boolean>(() => surfaceVuexStore?.state.xCommentsPanel)
  const commentsPanelPostId = computed<Id>(() => surfaceVuexStore?.state.commentsPanelPostId)
  const hideCommentsPanel = (): void => {
    void surfaceVuexStore?.dispatch('hideCommentsPanel')
  }

  return {
    commentIds,
    commentIdsByPost,
    commentBeingEditedId,
    commentsByPostId,
    isCommenting,
    inlineCommentInputLastTypedInMs,
    commentEntities,
    commentUnderActionId,
    activeNewCommentPostId,
    newCommentBodyForPostCid,
    editedDraftCommentBodyFromId,
    xCommentsPanel,
    commentsPanelPostId,

    // Actions
    createCommentV2,
    startEditingCommentV2,
    editCommentV2,
    approveComment,
    rejectComment,
    askToDeleteComment,
    fetchComments,
    refreshComments,
    showCommentActionMenu,
    hideCommentActionMenu,
    startEditingComment,
    stopEditingComment,
    startWritingNewComment,
    stopWritingNewComment,
    resetWritingNewComment,
    deleteComment,
    showSelfHarmCommentAlert,
    newCommentRemote,
    editCommentRemote,
    deleteCommentRemote,
    updateNewCommentBody,
    updateEditedDraftCommentBody,
    updateInlineCommentInputLastTypedInMs,
    hideCommentsPanel,
  }
})
